/**
 * Layout Composable
 * 管理版面配置（panel、mainMenu、quickLayout）
 *
 * 架構：
 * - 狀態管理：使用 Pinia store (stores/core/layout.js)
 * - API：從 commonApi.getLayoutConfig 獲取配置，透過 onResponse 同步處理資料
 * - 數據格式：Base64 編碼的 JSON，需要解碼
 * - 單例模式：使用 nuxtApp._layoutContext 避免 storeToRefs 重複創建
 */

import { storeToRefs } from 'pinia'
import { getCurrentInstance, onMounted, watch } from 'vue'
import { useFetch, useRuntimeConfig, useNuxtApp } from '#app'
import { useI18n } from '#imports'
import { useLayoutStore } from '@/stores/core/layout'
import { commonApi } from '@/api/common'
import { decode } from 'js-base64'
import { storage } from '@/utils/storage'

export const useLayout = () => {
  const nuxtApp = useNuxtApp()

  // 單例模式：確保 useFetch 和 storeToRefs 只創建一次
  if (!nuxtApp._layoutContext) {
    const { apiUrl, clientId, appSecret, intranet } = useRuntimeConfig().public
    const { locale } = useI18n()
    const store = useLayoutStore()

    // 初始同步 locale 到 store
    store.setLocale(locale.value)

    const decodeConfig = (base64String) => {
      try {
        const base64Url = base64String.split('.')[0]
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
        const decoded = decode(base64)
        return JSON.parse(decoded)
      } catch (error) {
        console.error('[useLayout] decodeConfig error:', error)
        throw new Error(`Failed to decode config: ${error.message}`)
      }
    }

    // 根據環境決定 API base URL
    const baseUrl = import.meta.server && intranet === '1' ? 'http://frontend-api-v2.default.svc.cluster.local' : apiUrl

    // 從 commonApi 取得 API 配置
    const apiConfig = commonApi.getLayoutConfig({ appSecret })
    const apiPath = apiConfig.url.replace(':clientId', clientId)

    // 使用 useFetch 自動執行（SSR 友善）
    // 使用 onResponse 確保 SSR 階段資料同步處理，避免 Hydration 時序問題
    useFetch(`${baseUrl}/${apiPath}`, {
      key: 'layout-config',
      dedupe: 'defer',
      query: apiConfig.params,
      getCachedData(key, nuxtApp) {
        return nuxtApp.payload.data[key] || nuxtApp.static.data[key]
      },
      onResponse({ response }) {
        if (response._data?.config) {
          const config = decodeConfig(response._data.config)
          store.setLayoutConfig(config)
        }
      },
      onResponseError({ error: err }) {
        console.error('[useLayout] fetchLayoutConfig API error:', err)
        store.setLayoutConfig({})
      },
    })

    // storeToRefs 只調用一次
    nuxtApp._layoutContext = {
      ...storeToRefs(store),
      // Internal (for client-side watch)
      _locale: locale,
      _store: store,
      _clientWatchCreated: false,
    }
  }

  const ctx = nuxtApp._layoutContext

  // 客戶端專屬：同步 locale 狀態
  // 每次 useLayout() 呼叫時直接同步，確保狀態一致
  if (import.meta.client) {
    // 直接同步 locale 狀態到 store
    ctx._store.setLocale(ctx._locale.value)

    // 創建 watch 監聽後續變化（如果還沒創建）
    if (!ctx._clientWatchCreated) {
      watch(ctx._locale, (newLocale) => {
        ctx._store.setLocale(newLocale)
      })
      ctx._clientWatchCreated = true
    }
  }

  // Client 端 mounted 後存儲 layoutConfig 到 localStorage
  if (getCurrentInstance()) {
    const store = useLayoutStore()
    onMounted(() => {
      if (store.layoutConfig) {
        storage.set('layout-config', store.layoutConfig)
      }
    })
  }

  return ctx
}
